<template>
  <!--Evento emitido quando o model vinculado é atualizado
      @event input
      @property {Boolean} model - indica o model atual do componente
   -->
  <v-menu
    :absolute="absolute"
    :auto="auto"
    :value="value"
    :top="top"
    :bottom="bottom"
    :left="left"
    :right="right"
    :close-delay="closeDelay"
    :close-on-click="closeOnClick"
    :close-on-content-click="closeOnContentClick"
    :dark="dark"
    :disabled="disabled"
    :rounded="rounded"
    :max-height="maxHeight"
    :max-width="maxWidth"
    :min-width="minWidth"
    :offset-x="offsetX"
    :offset-y="offsetY"
    :offset-overflow="offsetOverflow"
    :open-on-click="openOnClick"
    :open-on-hover="openOnHover"
    :transition="transition"
    :z-index="zIndex"
    :position-x="positionX"
    :position-y="positionY"
    :tile="tile"
    :nudge-bottom="nudgeBottom"
    :nudge-top="nudgeTop"
    :nudge-left="nudgeLeft"
    :nudge-right="nudgeRight"
    @input="$emit('input', $event)"
  >
    <template v-slot:activator="{on, attrs}">
      <!--@slot Slot para custmização da ação do componente-->
      <slot name="activator" v-bind="attrs" v-on="on">
        <z-btn
          text="dropdown"
          color="primary"
          primary
          v-bind="attrs"
          v-on="on"
        ></z-btn>
      </slot>
    </template>
    <v-list>
      <v-list-item
        v-for="item of items"
        :key="item.text"
        @click="onClickItem(item)"
      >
        {{ item.text }}
      </v-list-item>
    </v-list>
  </v-menu>
</template>

<script>
/**
 * @displayName Menu
 */
export default {
  name: 'ZMenu',
  model: {
    event: 'input',
  },
  props: {
    /**
     * Aplica `position: absolute` ao componente.
     */
    absolute: {
      type: Boolean,
      default: false,
    },
    /**
     * Controla se o componente está visível ou oculto.
     */
    value: {
      type: String,
      required: false,
    },
    /**
     * Centraliza a lista no elemento selecionado
     */
    auto: {
      type: Boolean,
      required: false,
    },
    /**
     * Alinha o conteúdo em direção ao topo.
     */
    top: {
      type: Boolean,
      default: false,
    },
    /**
     * Alinha o conteúdo em direção a parte inferior.
     */
    bottom: {
      type: Boolean,
      default: false,
    },
    /**
     * TAlinha o conteúdo em direção a esquerda.
     */
    left: {
      type: Boolean,
      default: false,
    },
    /**
     * Alinha o conteúdo em direção a direita.
     */
    right: {
      type: Boolean,
      default: false,
    },
    /**
     * Milissegundos de espera antes de fechar o componente. Funciona apenas com a prop `open-on-hover` ao passar o mouse
     */
    closeDelay: {
      type: [Number, String],
      default: 0,
    },
    /**
     * Milissegundos de espera antes de abrir o componente. Funciona apenas com a prop `open-on-hover` ao passar o mouse
     */
    openDelay: {
      type: [Number, String],
      default: 0,
    },
    /**
     * Flag que designa se o menu deve fechar ao clicar fora do activator
     */
    closeOnClick: {
      type: Boolean,
      default: true,
    },
    /**
     * Flag que designa se o menu deve fechar quando seu conteúdo for clicado
     */
    closeOnContentClick: {
      type: Boolean,
      default: true,
    },
    /**
     * Flag para aplicar o tema dark ao componente.
     */
    dark: {
      type: Boolean,
      default: false,
    },
    /**
     * Flag para desativar o componente.
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * Flag para aplicar um `border-radius` no componente.
     */
    rounded: {
      type: Boolean,
      default: false,
    },
    /**
     * Flag para definir a altura máxima do conteúdo do menu
     */
    maxHeight: {
      type: [Number, String],
      default: 'auto',
    },
    /**
     * Flag para definir a largura máxima do conteúdo do menu
     */
    maxWidth: {
      type: [Number, String],
      default: 'auto',
    },
    /**
     * Flag para definir a largura mínima do conteúdo do menu
     */
    minWidth: {
      type: [Number, String],
      required: false,
    },
    /**
     * Flag para deslocar o menu no eixo x. Funciona em conjunto com a direção left/right
     */
    offsetX: {
      type: Boolean,
      default: false,
    },
    /**
     * Flag para deslocar o menu no eixo y. Funciona em conjunto com a direção top/bottom
     */
    offsetY: {
      type: Boolean,
      default: false,
    },
    /**
     * Faz com que o componente vire para o lado oposto quando reposicionado devido ao overflow
     */
    offsetOverflow: {
      type: Boolean,
      default: false,
    },
    /**
     * Flag para designar se o menu deve abrir ao clicar no activator
     */
    openOnClick: {
      type: Boolean,
      default: true,
    },
    /**
     * Flag para designar se o menu deve abrir ao passar o mouse no activator
     */
    openOnHover: {
      type: Boolean,
      default: false,
    },
    /**
     * Flag para definir a transição do componente. Pode ser uma das transições incorporadas no `v-menu-transition` ou uma criada por você.
     */
    transition: {
      type: [Boolean, String],
      default: 'v-menu-transition',
    },
    /**
     * O z-index usado para o componente.
     */
    zIndex: {
      type: [Number, String],
      required: false,
    },
    /**
     * Flag para posicionar no eixo-x o conteúdo quando não estiver usando um slot activator.
     */
    positionX: {
      type: Number,
      required: false,
    },
    /**
     * Flagpara posicionar no eixo-y o conteúdo quando não estiver usando um slot de ativador.
     */
    positionY: {
      type: Number,
      required: false,
    },
    /**
     * Flag para esconder o `border-radius` do componente.
     */
    tile: {
      type: Boolean,
      default: false,
    },
    /**
     * Flag para deslocar o conteúdo para baixo.
     */
    nudgeBottom: {
      type: [Number, String],
      default: 0,
    },
    /**
     * Flag para deslocar o conteúdo para a esquerda.
     */
    nudgeLeft: {
      type: [Number, String],
      default: 0,
    },
    /**
     * Flag para deslocar o conteúdo para a direita.
     */
    nudgeRight: {
      type: [Number, String],
      default: 0,
    },
    /**
     * Flag para deslocar o conteúdo para cima.
     */
    nudgeTop: {
      type: [Number, String],
      default: 0,
    },
    /**
     * Flag para ajustar a largura do conteúdo.
     */
    nudgeWidth: {
      type: [Number, String],
      default: 0,
    },
    /**
     * Options of dropdown
     *
     * @typedef Object {text:string,value:any}
     */
    items: {
      type: Array,
      required: true,
    },
  },
  computed: {
    valueText() {
      if (this.value) {
        const selectedOption = this.items.find(
          (item) => item.value === this.value
        )

        return selectedOption.text
      }
      return this.text
    },
  },
  methods: {
    onClickItem(item) {
      /**
       * Evento emitido ao selecionar um item
       *
       * @property {any} itemValue - Valor do item selecionado
       */
      this.$emit('change', item.value)
    },
  },
}
</script>

<style></style>
